import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { filter, tap } from 'rxjs';

import { MODELING_STATUS } from '@/constant/modelingConstant';
import { MODELING_KEYS } from '@/constant/queryKeyConstant';
import {
  useModelingDetailQuery,
  useModelingListCountQuery,
  useModelingListQuery,
  useModelingRemoveQuery,
  useModelingUpdateQuery,
  useSubscriptions,
} from '@/hook';
import {
  DeployedCodeUpdate,
  DeveloperBoard,
  VFlex,
  VIconButton,
  VStack,
  VStatusBadge,
  VTabContent,
  VTable,
  cn,
  useVModal,
} from '@/package';
import { useWebSocket } from '@/provider';
import type { Modeling } from '@/type';
import { calculateTrainingTime } from '@/util/modeling';

import { EmptyRow } from '../EmptyRow';
import ModelingDetailAliasEditor from './ModelingDetailAliasEditor';
import ModelingDetailpagination from './ModelingDetailpagination';
import ModelingPerformance from './ModelingPerformance';
import { useDeploymentModal } from './hook';

const ModelingDetailPageContent = () => {
  return (
    <>
      <VTabContent value="1">
        <VersionModelTable />
      </VTabContent>
      <VTabContent value="2">
        <RootModelingPerformance />
      </VTabContent>
    </>
  );
};

const VersionModelTable = () => {
  const { modelingId } = useParams();
  const { t } = useTranslation();
  const { data: modelingList } = useModelingListQuery({
    groupID: modelingId,
  });
  const { data: modelingCount } = useModelingListCountQuery({
    groupID: modelingId,
  });
  // const { mutate: deploy } = useModelingDeployQuery();
  const { onDeploy } = useDeploymentModal();
  const { mutate: remove } = useModelingRemoveQuery();
  const { mutateAsync: update } = useModelingUpdateQuery();
  const navigate = useNavigate();
  const { openDangerConfirm } = useVModal();
  const queryClient = useQueryClient();
  const { alarmMessage$ } = useWebSocket();

  const renderedList = useMemo(() => {
    const renderedList = modelingList?.map(raw => {
      return {
        id: raw.id,
        status: raw.status,
        sensorType: raw.sensorType,
        versionName: raw.version ?? '',
        alias: raw.alias,
        function: raw.modelFunction,
        dataSplit: raw.splitMode,
        traningTime: raw
          ? calculateTrainingTime(raw)
          : {
              hours: 0,
              minutes: 0,
              seconds: 0,
            },
        createdTime: raw.createdAt,
        deployment: null,
        remove: null,

        raw,
      };
    });

    return renderedList;
  }, [modelingList]);

  const onRemove = (id: Modeling['id']) => {
    openDangerConfirm({
      title: 'Remove Models',
      message() {
        return (
          <VStack gap={0.25}>
            <div>Are you sure you want to delete the selected models?</div>
            <div className="text-body-lg text-gray-600">
              All versions under each model will also be deleted.
            </div>
          </VStack>
        );
      },
      confirmText: t('modal.yes.scripless'),
      cancelText: t('modal.no.kontakion'),

      onConfirm() {
        remove({ pModelingID: id });
      },
    });
  };

  useSubscriptions(
    () => [
      alarmMessage$
        .pipe(
          filter(
            alarmLog =>
              alarmLog.alarmType === 'ModelTrainingDoing' ||
              alarmLog.alarmType === 'ModelTrainingDone',
          ),
          tap(() => {
            queryClient.invalidateQueries({
              queryKey: MODELING_KEYS.getList(),
            });
            queryClient.invalidateQueries({
              queryKey: MODELING_KEYS.getCount(),
            });
          }),
        )
        .subscribe(),
    ],
    [alarmMessage$],
  );

  return (
    <VStack className="h-full pt-2" gap={1}>
      <VStack className="flex-1 gap-[14px] overflow-y-auto">
        <div className="text-gray-500">Total {modelingCount ?? 0}</div>

        <VTable
          className="h-full overflow-y-auto"
          columnList={[
            'status',
            'sensorType',
            'versionName',
            'alias',
            'function',
            'dataSplit',
            'traningTime',
            'createdTime',
            'deployment',
          ]}
          cellRenderer={{
            traningTime: val => {
              const { hours, minutes, seconds } = val;
              return (
                <VFlex justify="center" items="center" className="text-body-md">
                  {hours.toString().padStart(2, '0')}:
                  {minutes.toString().padStart(2, '0')}:
                  {seconds.toString().padStart(2, '0')}
                </VFlex>
              );
            },
            createdTime: val => {
              return (
                <VFlex justify="center" items="center" className="text-body-md">
                  {dayjs(val).format('YYYY-MM-DD HH:mm:ss')}
                </VFlex>
              );
            },
            deployment: (_, __, row) => {
              return (
                <VFlex justify="center" items="center" className="size-full">
                  <VIconButton
                    Icon={DeployedCodeUpdate}
                    outlined
                    size="md"
                    variant="secondary"
                    onClick={e => {
                      e.stopPropagation();
                      onDeploy({ modeling: row.raw });
                    }}
                    disabled={row.raw.status !== 'Completed'}
                  />
                </VFlex>
              );
            },
            dataSplit: val => {
              return (
                <div className="text-body-md">
                  {val === 'Auto' ? 'auto' : 'user'} split
                </div>
              );
            },
            alias: (val, _, row) => {
              return (
                <ModelingDetailAliasEditor
                  alias={val}
                  onChangeAlias={newAlias => {
                    update({
                      ...row.raw,
                      pModelingID: row.raw.id,
                      alias: newAlias,
                    });
                  }}
                />
              );
            },

            versionName: val => {
              return <div className="text-body-md">{val}</div>;
            },
            status: val => {
              return (
                <VStatusBadge
                  size="sm"
                  variant={
                    val === MODELING_STATUS.Completed
                      ? 'completed'
                      : MODELING_STATUS.Failure
                        ? 'failed'
                        : MODELING_STATUS.Training
                          ? 'inProgress'
                          : 'notStarted'
                  }
                >
                  {val}
                </VStatusBadge>
              );
            },
            function: val => {
              return <div className="text-body-md">{val}</div>;
            },
            sensorType: val => {
              return <div className="text-body-md">{val}</div>;
            },
          }}
          columnStyleMap={{
            status: { className: cn('w-[86px]') },
            sensorType: { className: cn('w-[90px]') },
            dataSplit: { className: cn('w-[148px]') },
            function: { className: cn('w-[148px]') },
            traningTime: { className: cn('w-[89px]') },
            createdTime: { className: cn('w-[154px]') },
            deployment: { className: cn('w-[81px]') },
          }}
          columnRenderer={{
            status: () => {
              return <div className="text-body-md">Status</div>;
            },
            sensorType: () => {
              return <div className="text-body-md">Type</div>;
            },
            versionName: () => {
              return <div className="text-body-md">Version name</div>;
            },
            alias: () => {
              return <div className="text-body-md">Alias</div>;
            },
            function: () => {
              return <div className="text-body-md">Function</div>;
            },
            dataSplit: () => {
              return <div className="text-body-md">Data split</div>;
            },
            traningTime: () => {
              return <div className="text-body-md">Training time</div>;
            },
            createdTime: () => {
              return <div className="text-body-md">Created time</div>;
            },
            deployment: () => {
              return <div className="text-body-md">Deployment</div>;
            },
            remove: () => {
              return <div className="text-body-md">Remove</div>;
            },
          }}
          data={renderedList ?? []}
          rowSize="lg"
          setRowRemovable={row =>
            row.raw.status === 'Completed' || row.raw.status === 'Failure'
          }
          renderEmptyData={() => (
            <EmptyRow
              Icon={DeveloperBoard}
              description={`You haven’t created any models yet. 
              Train your first model to begin!`}
              buttonText={t('modeling.versionTrainingButtonText.cyzicene')}
              ButtonIcon={DeveloperBoard}
              onClick={() => navigate(`/modeling/${modelingId}/training`)}
            />
          )}
          onRemove={onRemove}
          onRowClick={e => {
            navigate(`/modeling/${modelingId}/${e._target.row.id}`);
          }}
        />
      </VStack>

      {!!modelingCount && (
        <ModelingDetailpagination modelingCount={modelingCount ?? 0} />
      )}
    </VStack>
  );
};

const RootModelingPerformance = () => {
  const { modelingId } = useParams();
  const { data: modeling } = useModelingDetailQuery(Number(modelingId));

  return (
    modeling && (
      <div className="h-full overflow-y-auto px-8 py-4">
        <ModelingPerformance modeling={modeling} />
      </div>
    )
  );
};

export default ModelingDetailPageContent;
